import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { RotateCcw } from 'lucide-react';

/**
 * Renders a Goldsmith Return Memo number as a clickable link to
 * `/return-memos/:id`.
 *
 * Accepts either:
 *  - `id` (the goldsmith_returns.id / return_id) — preferred when known.
 *  - `memoNo` (the return_memo_no string like R-TES/JUNE/26-27/002) — will
 *    resolve to id via `/api/goldsmith-issues/returns/by-memo/<memo_no>` if
 *    `id` is not provided.
 *
 * Falls back to a faded chip when the memo cannot be resolved (e.g. legacy
 * data without a proper return doc).
 */
export default function ReturnMemoLink({
  id,
  memoNo,
  fallback = '—',
  className = '',
  testId,
  iconSize = 10,
}) {
  const [resolvedId, setResolvedId] = useState(id || null);
  const lastQueriedRef = useRef(null);

  useEffect(() => {
    if (id) {
      Promise.resolve().then(() => setResolvedId(id));
      return;
    }
    if (!memoNo) {
      Promise.resolve().then(() => setResolvedId(null));
      return;
    }
    if (lastQueriedRef.current === memoNo) return;
    lastQueriedRef.current = memoNo;
    let cancelled = false;
    api
      .get(`/goldsmith-issues/returns/by-memo/${encodeURIComponent(memoNo)}`)
      .then(({ data }) => { if (!cancelled) setResolvedId(data?.id || null); })
      .catch(() => { if (!cancelled) setResolvedId(null); });
    return () => { cancelled = true; };
  }, [id, memoNo]);

  const label = memoNo || '—';
  const baseCls = `inline-flex items-center gap-1 font-mono text-[10px] px-1.5 py-0.5 rounded ${className}`;

  if (resolvedId) {
    return (
      <Link
        to={`/return-memos/${resolvedId}`}
        className={`${baseCls} bg-rose-50 text-rose-700 hover:bg-rose-100 hover:underline`}
        data-testid={testId || 'return-memo-link'}
        title={`Open return memo ${label}`}
      >
        <RotateCcw size={iconSize} />
        {label}
      </Link>
    );
  }
  return (
    <span
      className={`${baseCls} bg-slate-100 text-slate-500`}
      data-testid={testId || 'return-memo-link'}
      title="Return memo unavailable"
    >
      <RotateCcw size={iconSize} />
      {label || fallback}
    </span>
  );
}
